/**
 * Type Guards Centralisés
 * Fonctions de validation à l'exécution pour les types TypeScript
 */

import { 
  Role, 
  Permission, 
  UserStatus, 
  ProjectStatus, 
  InvoiceStatus,
  MetricType,
  DataVisibilityLevel
} from '@/types';

/**
 * TYPE-001: Type guards corrigés avec cast approprié
 */

/**
 * Type guard pour valider qu'une valeur est un Role valide
 */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' &&
         (Object.values(Role) as string[]).includes(value);
}

/**
 * Type guard pour valider qu'une valeur est une Permission valide
 */
export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' &&
         (Object.values(Permission) as string[]).includes(value);
}

/**
 * Type guard pour valider un tableau de permissions
 */
export function isPermissionArray(value: unknown): value is Permission[] {
  return Array.isArray(value) && value.every(isPermission);
}

/**
 * Filtre et valide les permissions d'un utilisateur
 * Retourne uniquement les permissions valides
 */
export function sanitizePermissions(permissions: unknown): Permission[] {
  if (!Array.isArray(permissions)) return [];
  return permissions.filter(isPermission);
}

/**
 * Type guard pour UserStatus
 */
export function isUserStatus(value: unknown): value is UserStatus {
  return typeof value === 'string' &&
         (Object.values(UserStatus) as string[]).includes(value);
}

/**
 * Type guard pour ProjectStatus
 */
export function isProjectStatus(value: unknown): value is ProjectStatus {
  return typeof value === 'string' &&
         (Object.values(ProjectStatus) as string[]).includes(value);
}

/**
 * Type guard pour InvoiceStatus
 */
export function isInvoiceStatus(value: unknown): value is InvoiceStatus {
  return typeof value === 'string' &&
         (Object.values(InvoiceStatus) as string[]).includes(value);
}

/**
 * Type guard pour MetricType
 */
export function isMetricType(value: unknown): value is MetricType {
  return typeof value === 'string' &&
         (Object.values(MetricType) as string[]).includes(value);
}

/**
 * Type guard pour DataVisibilityLevel
 */
export function isDataVisibilityLevel(value: unknown): value is DataVisibilityLevel {
  return typeof value === 'string' &&
         (Object.values(DataVisibilityLevel) as string[]).includes(value);
}

/**
 * Type guard générique pour vérifier qu'un objet contient les clés requises
 */
export function hasRequiredKeys<T extends Record<string, unknown>>(
  obj: unknown,
  keys: string[]
): obj is T {
  if (!obj || typeof obj !== 'object') return false;
  return keys.every(key => key in obj);
}

/**
 * Type guard pour valider les données du profil venant de Supabase
 */
export function isValidProfileData(data: unknown): data is {
  id: string;
  role: Role;
  company_id?: string;
  status: string;
  permissions: Permission[];
  default_expense_type_id?: string | null;
  default_subcategory_id?: string | null;
} {
  if (!data || typeof data !== 'object') return false;
  
  const profile = data as Record<string, unknown>;
  
  // Vérifications obligatoires
  if (typeof profile.id !== 'string') return false;
  if (!isRole(profile.role)) return false;
  if (typeof profile.status !== 'string') return false;
  
  // Vérifications optionnelles
  if (profile.company_id !== undefined && profile.company_id !== null && typeof profile.company_id !== 'string') {
    return false;
  }
  
  if (profile.default_expense_type_id !== undefined && 
      profile.default_expense_type_id !== null && 
      typeof profile.default_expense_type_id !== 'string') {
    return false;
  }
  
  if (profile.default_subcategory_id !== undefined && 
      profile.default_subcategory_id !== null && 
      typeof profile.default_subcategory_id !== 'string') {
    return false;
  }
  
  // Permissions doit être un tableau (peut être vide)
  if (!Array.isArray(profile.permissions)) return false;
  
  return true;
}

/**
 * Type guard pour vérifier qu'une valeur est une string non vide
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Type guard pour vérifier qu'une valeur est un nombre valide
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

/**
 * Type guard pour vérifier qu'une valeur est un nombre positif
 */
export function isPositiveNumber(value: unknown): value is number {
  return isValidNumber(value) && value > 0;
}

/**
 * Type guard pour vérifier qu'une valeur est une date ISO valide
 */
export function isISODateString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
}